import React, { useMemo } from "react";
import { Container, Col, Row } from "reactstrap";
import { useQuery } from "@apollo/client";
import { Fields } from "@repoflow/components/fields";
import { useSectionContext } from "@repoflow/components/section";
import { Link } from "react-router-dom";
import { Composed } from "components/preview/composed";
import * as widgets from "widgets/register";
import * as PageQL from "queries/page";
import * as SiteQL from "queries/site";

import { GROUP_SCAN, Translations, groupMembers } from "../translations";
import { LiveLink, useLiveHost } from "../../live";
import { ExportButton } from "./export";
import { PublishButton, PublishPanel } from "./publish";

export const Sidebar = () => null;

export const Indice = () => null;

// What this screen states about the page itself.
//
// Hoisted and exported for the reason the listing's LIST_FIELDS is: a literal
// in JSX is a new config object every render, and a test carrying its own copy
// passes while the screen is wired to something else.
//
// `translationKey` is NOT on it, and that is the point of the table below. The
// stored value is a NAME — "pricing" on /pricing, /es/precios and /de/preise
// alike — but nobody has to type one: `PageEntityMutations.translate` mints one
// from the source's `pageId` when the source has none, so the commonest page in
// a group carries `9606858d651b46c19947a7b255266205`. Printed as a field, that
// is an internal id sitting between the route and the preview, answering a
// question nobody asked in a form nobody can read. The question an author
// actually holds is "which languages is this page in, and where are they", and
// the group's own rows answer it. It is on no other screen either — the Page
// tab asked for it until it was clear that what the box invited was an edit,
// and an edit of it moves a page between groups silently. Nothing but
// `translate` writes it now.
export const VIEW_FIELDS = {
  "/Public/Page": {
    fields: ["pageId", "title", "route", "locale"],
  },
};

// The page, what it renders as, and the two things you can do to it.
//
// A page owns nothing — no child collection, no derived figures — so this screen
// is the entity and its actions and stops there. That is deliberate rather than
// unfinished: the service this was derived from grew a statistics block and two
// nested listings on the equivalent screen, and both listings duplicated a
// section one click away.
//
// The composed document is on the screen rather than behind a Preview button
// leading to one of its own. It is the only part of a page a person can read —
// the elements are a list of blobs — so a screen that showed those and offered
// a link to the rendered thing put the answer one click further away than the
// question.
export const Content = ({
  match: {
    params: { siteId },
  },
}) => {
  const { values, schema, base } = useSectionContext();

  // The site, for the language a page with no locale of its own resolves to and
  // for what this site calls its languages. `cache-first` and no error handling,
  // the same call the listing and the Translate screen make: every screen above
  // this one resolves the same query, so this is a cache read, and a site that
  // has not answered degrades to `localeOf`'s own fallback rather than to a
  // broken screen.
  const { data: siteData } = useQuery(SiteQL.get, {
    variables: { siteId },
    fetchPolicy: "cache-first",
  });

  // The site's pages, and only when this one is in a group — a page nobody has
  // translated asks the server nothing. Same variables as the Translate screen
  // sends, so the two share one cache entry and walking from here to there is
  // free.
  const { data: pagesData } = useQuery(PageQL.list, {
    variables: { siteId, input: { limit: GROUP_SCAN } },
    fetchPolicy: "cache-first",
    skip: !(values && values.translationKey),
  });

  const site = siteData ? siteData.viewer.account.sites.get : null;

  // The hostname this page is published on, "" until a domain is verified —
  // see ../../live for why nothing is drawn in that state.
  const host = useLiveHost(siteId);

  // The unwrap is INSIDE the memo, not above it: a `||` on the outside is a
  // fresh array identity on every render, which is a dependency that always
  // differs and a memo that never holds. The Translate screen's is written the
  // same way and says the same thing.
  const members = useMemo(
    () =>
      groupMembers(
        (pagesData && pagesData.viewer.account.sites.get.pages.list.items) || [],
        values
      ),
    [pagesData, values]
  );

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <div className="float-right">
            {/* First, and the only one of these that leaves the app: the
                preview below this is what the page looks like composed here,
                and this is the same page delivered by the service on its own
                hostname. The two disagreeing is worth one click to find out. */}
            <LiveLink
              host={host}
              page={values}
              // Icon only, as a link: it is the one control here that leaves
              // the app, and it should not read as one more button in the row
              // that acts on the record. Named for the specs and for a screen
              // reader; the hostname it goes to is in the title.
              className="btn btn-link"
              aria-label="Open"
            >
              <i className="fa fa-external-link" aria-hidden="true" />
            </LiveLink>{" "}
            {/* First among the buttons, because it is what a save leads to:
                the editor writes the draft, and this is what makes the draft
                the page. Lit only while the draft differs from what is served
                — decided by the API, shown here. */}
            <PublishButton siteId={siteId} pageId={values.pageId} />{" "}
            <Link className="btn btn-primary" to={`${base}/edit`}>
              Edit
            </Link>{" "}
            {/* Here rather than on the editor's tab strip: translating produces
                a DIFFERENT page, so it belongs beside Remove among the things
                that act on this entity, not among the things that change it. */}
            <Link className="btn btn-secondary" to={`${base}/translate`}>
              Translate
            </Link>{" "}
            {/* Beside Translate rather than on the editor's tab strip, and for
                the same reason: exporting acts on this page AND every other
                language of it, so it belongs among the things that act on the
                entity rather than among the things that change one field of
                it. */}
            <ExportButton siteId={siteId} page={values} />{" "}
            {/* The other half of Export, and HERE rather than only on the
                listing because where import is invoked decides what it does:
                from this screen the file is expected to be THIS page's data
                and updates it in place. The listing's Import is the one that
                creates new pages. */}
            <Link className="btn btn-secondary" to={`${base}/import`}>
              Import
            </Link>{" "}
            <Link className="btn btn-danger" to={`${base}/remove`}>
              Remove
            </Link>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Fields
            schema={schema}
            values={values}
            widgets={widgets}
            target={{ $ref: "/Public/Page" }}
            config={VIEW_FIELDS}
          />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>
          {/* What visitors see, and the kept versions to roll back to. Below
              the fields rather than beside the buttons: the state is a
              sentence and the versions are a table, and neither fits a
              button row. */}
          <PublishPanel siteId={siteId} pageId={values.pageId} />
        </Col>
      </Row>
      <Row>
        <Col>
          {/* The other languages of this page, in place of the group's name.
              Nothing at all when there are none, which is every page nobody has
              translated — the Translate button above is what that page has to
              say about its translations. */}
          <Translations
            siteId={siteId}
            site={site}
            members={members}
            heading="Other languages"
          />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>
          <h6 className="text-muted mb-3">Preview</h6>
          {/* The page in the form the visitor gets it, composed in the browser
              out of the same modules the API serves it with. */}
          <Composed siteId={siteId} page={values} />
        </Col>
      </Row>
    </Container>
  );
};
